import React, { useEffect, useMemo, useState } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Users, UserCheck, Clock, Search, CalendarClock, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { adminService } from "@/pagesadmin/adminApi";
import axios from "@/lib/axios";

// @origin: v2 | @since: 3d9ebf6d, c064a0da, ac6ee83f, 0d9c5520 | @used-by: ALL
// @purpose: 2f port -- global + per-customer page-time drill-down panel.
// Ported verbatim from donor tip (5fe70ff4); backend counterpart is
// los_app.apis.admin_page_time_analytics. IST display timing (ac6ee83f) and
// sortable per-customer table (0d9c5520) already reflected in this version.

interface GlobalPageRow {
	route: string;
	avg_seconds: number | null;
	avg_display: string;
	total_visits: number;
	unique_users: number;
}

interface UserPageRow {
	route: string;
	avg_seconds: number | null;
	avg_display: string;
	total_seconds: number;
	visits: number;
	first_seen: string | null;
	last_seen: string | null;
}

interface PageTimeData {
	global_pages: GlobalPageRow[];
	user_pages: UserPageRow[] | null;
	user_overall_avg_seconds: number | null;
	user_overall_avg_display: string;
}

interface UserOption {
	id: number;
	first_name: string;
	last_name: string;
	business_name: string | null;
	is_staff?: boolean;
	is_superuser?: boolean;
}

type UserPageSortKey = keyof Pick<
	UserPageRow,
	"route" | "avg_seconds" | "total_seconds" | "visits" | "first_seen" | "last_seen"
>;

const formatDateTime = (iso: string | null): string => {
	if (!iso) return "—";
	try {
		const d = new Date(iso);
		return d.toLocaleString("en-IN", {
			timeZone: "Asia/Kolkata",
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	} catch {
		return "—";
	}
};

const PageTimeAnalytics: React.FC = () => {
	const [globalLoading, setGlobalLoading] = useState(true);
	const [globalData, setGlobalData] = useState<GlobalPageRow[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [excludeStaffLikeGlobal, setExcludeStaffLikeGlobal] = useState<boolean>(true);

	const [users, setUsers] = useState<UserOption[]>([]);
	const [usersLoading, setUsersLoading] = useState(true);
	const [selectedUserId, setSelectedUserId] = useState<string>("");
	const [userLoading, setUserLoading] = useState(false);
	const [userData, setUserData] = useState<PageTimeData | null>(null);
	const [search, setSearch] = useState("");
	const [excludeStaffLikeCustomers, setExcludeStaffLikeCustomers] = useState<boolean>(true);

	const [userPageSort, setUserPageSort] = useState<{ key: UserPageSortKey; direction: "asc" | "desc" }>({
		key: "visits",
		direction: "desc",
	});

	// Fetch global page time analytics on mount / whenever the exclude toggle changes
	useEffect(() => {
		const fetchGlobal = async () => {
			setGlobalLoading(true);
			try {
				const res = await adminService.getPageTimeAnalytics({
					exclude_staff_like: excludeStaffLikeGlobal,
				});
				setGlobalData(res.data.global_pages || []);
				setError(null);
			} catch (err) {
				console.error(err);
				setError("Failed to load page time analytics");
			} finally {
				setGlobalLoading(false);
			}
		};
		fetchGlobal();
	}, [excludeStaffLikeGlobal]);

	// Fetch users list for dropdown
	useEffect(() => {
		const fetchUsers = async () => {
			setUsersLoading(true);
			try {
				const res = await axios.post("/api/public/users/");
				setUsers(res.data as UserOption[]);
			} catch (err) {
				console.error("Failed to load users", err);
			} finally {
				setUsersLoading(false);
			}
		};
		fetchUsers();
	}, []);

	// Fetch per-user drill-down when selectedUserId changes
	useEffect(() => {
		if (!selectedUserId) {
			setUserData(null);
			return;
		}
		const fetchUserData = async () => {
			setUserLoading(true);
			try {
				const res = await adminService.getPageTimeAnalytics({ user_id: Number(selectedUserId) });
				setUserData(res.data);
			} catch (err) {
				console.error(err);
			} finally {
				setUserLoading(false);
			}
		};
		fetchUserData();
	}, [selectedUserId]);

	// Set of (first_name, last_name) pairs belonging to staff/superuser accounts,
	// used to also exclude "look-alike" customer accounts sharing a staff member's name.
	const staffNamePairs = useMemo(() => {
		const pairs = new Set<string>();
		users.forEach((u) => {
			if (u.is_staff || u.is_superuser) {
				const fn = (u.first_name || "").trim().toLowerCase();
				const ln = (u.last_name || "").trim().toLowerCase();
				if (fn || ln) pairs.add(`${fn}|${ln}`);
			}
		});
		return pairs;
	}, [users]);

	const isStaffLike = (u: UserOption): boolean => {
		if (u.is_staff || u.is_superuser) return true;
		const fn = (u.first_name || "").trim().toLowerCase();
		const ln = (u.last_name || "").trim().toLowerCase();
		if (!fn && !ln) return false;
		return staffNamePairs.has(`${fn}|${ln}`);
	};

	const filteredUsers = users.filter((u) => {
		if (excludeStaffLikeCustomers && isStaffLike(u)) return false;
		if (!search) return true;
		const fullName = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
		const bName = (u.business_name || "").toLowerCase();
		const q = search.toLowerCase();
		return fullName.includes(q) || bName.includes(q);
	});

	const selectedUser = users.find((u) => String(u.id) === selectedUserId);

	// Overall first-ever / last-ever visit across ALL pages for the selected customer
	const overallFirstLastSeen = useMemo(() => {
		if (!userData?.user_pages || userData.user_pages.length === 0) {
			return { first: null as string | null, last: null as string | null };
		}
		let first: string | null = null;
		let last: string | null = null;
		for (const row of userData.user_pages) {
			if (row.first_seen && (!first || row.first_seen < first)) first = row.first_seen;
			if (row.last_seen && (!last || row.last_seen > last)) last = row.last_seen;
		}
		return { first, last };
	}, [userData]);

	const handleUserPageSort = (key: UserPageSortKey) => {
		setUserPageSort((prev) => {
			if (prev.key === key) {
				return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
			}
			return { key, direction: "desc" };
		});
	};

	const UserPageSortIcon = ({ column }: { column: UserPageSortKey }) => {
		if (userPageSort.key !== column)
			return <ArrowUpDown className="h-3.5 w-3.5 text-gray-300 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity" />;
		return userPageSort.direction === "asc" ? (
			<ArrowUp className="h-3.5 w-3.5 text-intellend-green ml-1.5" />
		) : (
			<ArrowDown className="h-3.5 w-3.5 text-intellend-green ml-1.5" />
		);
	};

	const sortedUserPages = useMemo(() => {
		if (!userData?.user_pages) return [];
		const rows = [...userData.user_pages];
		const { key, direction } = userPageSort;
		const modifier = direction === "asc" ? 1 : -1;
		rows.sort((a, b) => {
			const aVal = a[key];
			const bVal = b[key];
			if (aVal === null && bVal === null) return 0;
			if (aVal === null) return 1;
			if (bVal === null) return -1;
			if (typeof aVal === "string" && typeof bVal === "string") {
				return aVal.localeCompare(bVal) * modifier;
			}
			return ((aVal as number) < (bVal as number) ? -1 : 1) * modifier;
		});
		return rows;
	}, [userData, userPageSort]);

	return (
		<div className="space-y-6">
			{/* Section 1: Global */}
			<Card className="border-intellend-green/10 shadow-sm overflow-hidden relative">
				{globalLoading && (
					<div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-10">
						<Loader2 className="h-6 w-6 animate-spin text-intellend-green" />
					</div>
				)}
				<CardHeader className="pb-3">
					<div className="flex items-center gap-2">
						<Users className="h-5 w-5 text-intellend-green" />
						<CardTitle className="text-lg font-bold text-gray-900">Avg Time per Page — All Customers</CardTitle>
					</div>
					<CardDescription>
						Regardless of individual customer, this shows the average time spent on each page across everyone.
					</CardDescription>
					<div className="flex items-center gap-2 pt-2">
						<Checkbox
							id="exclude-staff-like-global"
							checked={excludeStaffLikeGlobal}
							onCheckedChange={(checked) => setExcludeStaffLikeGlobal(checked === true)}
						/>
						<label
							htmlFor="exclude-staff-like-global"
							className="text-sm font-medium text-gray-700 cursor-pointer select-none"
						>
							Exclude staff / superuser accounts (and any customer accounts sharing a staff member's name)
						</label>
					</div>
				</CardHeader>
				<CardContent className="p-0">
					{error ? (
						<div className="p-4 bg-red-50 text-red-700 m-4 rounded-md border border-red-200">{error}</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow className="bg-gray-50">
									<TableHead className="font-semibold text-gray-700">Route</TableHead>
									<TableHead className="font-semibold text-gray-700 text-right">Avg Time / Visit</TableHead>
									<TableHead className="font-semibold text-gray-700 text-right">Total Visits</TableHead>
									<TableHead className="font-semibold text-gray-700 text-right">Unique Users</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{globalData.length === 0 ? (
									<TableRow>
										<TableCell colSpan={4} className="text-center py-12 text-gray-400">
											No page visit data yet.
										</TableCell>
									</TableRow>
								) : (
									globalData.map((row) => (
										<TableRow key={row.route} className="hover:bg-gray-50/60 transition-colors">
											<TableCell className="font-mono text-xs text-gray-700">{row.route}</TableCell>
											<TableCell className="text-right font-mono text-intellend-green font-semibold">{row.avg_display}</TableCell>
											<TableCell className="text-right font-mono">{row.total_visits}</TableCell>
											<TableCell className="text-right font-mono">{row.unique_users}</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{/* Section 2: Per-customer drill-down */}
			<Card className="border-intellend-green/10 shadow-sm overflow-hidden">
				<CardHeader className="pb-3">
					<div className="flex items-center gap-2">
						<UserCheck className="h-5 w-5 text-intellend-green" />
						<CardTitle className="text-lg font-bold text-gray-900">Per-Customer Page Time Drill-down</CardTitle>
					</div>
					<CardDescription>
						Select a customer below to see how much time they spent on each page, and their overall average time per page.
					</CardDescription>
					<div className="flex items-center gap-2 pt-2">
						<Checkbox
							id="exclude-staff-like-customers"
							checked={excludeStaffLikeCustomers}
							onCheckedChange={(checked) => setExcludeStaffLikeCustomers(checked === true)}
						/>
						<label
							htmlFor="exclude-staff-like-customers"
							className="text-sm font-medium text-gray-700 cursor-pointer select-none"
						>
							Exclude staff / superuser accounts from this dropdown (and any customer accounts sharing a staff member's name)
						</label>
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex flex-col sm:flex-row gap-3">
						<div className="relative flex-1 max-w-sm">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
							<input
								placeholder="Search name or business…"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-intellend-green/20 focus:border-intellend-green transition-all"
							/>
						</div>
						<Select value={selectedUserId} onValueChange={setSelectedUserId} disabled={usersLoading}>
							<SelectTrigger className="w-full sm:w-[380px] bg-white border-gray-200">
								<SelectValue placeholder={usersLoading ? "Loading users…" : "Select a customer…"} />
							</SelectTrigger>
							<SelectContent className="max-h-72">
								{filteredUsers.map((u) => {
									const fullName = (u.first_name || u.last_name) ? `${u.first_name} ${u.last_name}`.trim() : `User #${u.id}`;
									return (
										<SelectItem key={u.id} value={String(u.id)} className="text-sm">
											<span className="font-medium">{fullName}</span>
											{u.business_name && <span className="text-gray-400 ml-2">({u.business_name})</span>}
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>
					</div>

					{!selectedUserId ? (
						<div className="flex flex-col items-center justify-center py-16 text-gray-400 bg-gray-50/50 rounded-xl border border-dashed">
							<UserCheck className="h-10 w-10 mb-3 opacity-30" />
							<p className="text-sm font-medium">Pick a customer from the dropdown above to view their page time breakdown.</p>
						</div>
					) : userLoading ? (
						<div className="flex items-center justify-center py-16">
							<Loader2 className="h-6 w-6 animate-spin text-intellend-green" />
						</div>
					) : userData ? (
						<div className="space-y-4">
							{/* Summary Cards */}
							<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
								<div className="flex items-center gap-4 p-4 bg-intellend-green/5 rounded-xl border border-intellend-green/20">
									<div className="p-3 bg-intellend-green/10 rounded-lg">
										<Clock className="h-5 w-5 text-intellend-green" />
									</div>
									<div>
										<p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
											Avg time spent per page — {selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}`.trim() : "this customer"}
										</p>
										<p className="text-2xl font-bold text-gray-900">{userData.user_overall_avg_display}</p>
									</div>
								</div>
								<div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
									<div className="p-3 bg-blue-100 rounded-lg">
										<CalendarClock className="h-5 w-5 text-blue-600" />
									</div>
									<div>
										<p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">First Ever Visit (IST)</p>
										<p className="text-base font-bold text-gray-900">{formatDateTime(overallFirstLastSeen.first)}</p>
									</div>
								</div>
								<div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
									<div className="p-3 bg-amber-100 rounded-lg">
										<CalendarClock className="h-5 w-5 text-amber-600" />
									</div>
									<div>
										<p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Ever Visit (IST)</p>
										<p className="text-base font-bold text-gray-900">{formatDateTime(overallFirstLastSeen.last)}</p>
									</div>
								</div>
							</div>

							<Table>
								<TableHeader>
									<TableRow className="bg-gray-50">
										<TableHead className="font-semibold text-gray-700 group cursor-pointer" onClick={() => handleUserPageSort("route")}>
											<div className="flex items-center">
												Route <UserPageSortIcon column="route" />
											</div>
										</TableHead>
										<TableHead className="font-semibold text-gray-700 text-right group cursor-pointer" onClick={() => handleUserPageSort("avg_seconds")}>
											<div className="flex items-center justify-end">
												Avg Time <UserPageSortIcon column="avg_seconds" />
											</div>
										</TableHead>
										<TableHead className="font-semibold text-gray-700 text-right group cursor-pointer" onClick={() => handleUserPageSort("total_seconds")}>
											<div className="flex items-center justify-end">
												Total Time <UserPageSortIcon column="total_seconds" />
											</div>
										</TableHead>
										<TableHead className="font-semibold text-gray-700 text-right group cursor-pointer" onClick={() => handleUserPageSort("visits")}>
											<div className="flex items-center justify-end">
												Visits <UserPageSortIcon column="visits" />
											</div>
										</TableHead>
										<TableHead className="font-semibold text-gray-700 group cursor-pointer" onClick={() => handleUserPageSort("first_seen")}>
											<div className="flex items-center">
												First Visit (IST) <UserPageSortIcon column="first_seen" />
											</div>
										</TableHead>
										<TableHead className="font-semibold text-gray-700 group cursor-pointer" onClick={() => handleUserPageSort("last_seen")}>
											<div className="flex items-center">
												Last Visit (IST) <UserPageSortIcon column="last_seen" />
											</div>
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{sortedUserPages.length === 0 ? (
										<TableRow>
											<TableCell colSpan={6} className="text-center py-12 text-gray-400">
												No page visit data for this customer yet.
											</TableCell>
										</TableRow>
									) : (
										sortedUserPages.map((row) => (
											<TableRow key={row.route} className="hover:bg-gray-50/60 transition-colors">
												<TableCell className="font-mono text-xs text-gray-700">{row.route}</TableCell>
												<TableCell className="text-right font-mono text-intellend-green font-semibold">{row.avg_display}</TableCell>
												<TableCell className="text-right font-mono text-gray-600">{Math.round(row.total_seconds)}s</TableCell>
												<TableCell className="text-right font-mono">{row.visits}</TableCell>
												<TableCell className="text-xs text-gray-600 whitespace-nowrap">{formatDateTime(row.first_seen)}</TableCell>
												<TableCell className="text-xs text-gray-600 whitespace-nowrap">{formatDateTime(row.last_seen)}</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</div>
					) : null}
				</CardContent>
			</Card>
		</div>
	);
};

export default PageTimeAnalytics;
